/**
 * TerraMatrix Universal Data Platform - useDataSources Hook
 *
 * Sprint 5: Frontend - Data Page
 * Created: November 2025
 *
 * React hook for managing data sources.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { dataApi, DataSource } from '@/services/api/dataApi';

export function useDataSources() {
  const queryClient = useQueryClient();

  // Fetch all data sources
  const sourcesQuery = useQuery({
    queryKey: ['dataSources'],
    queryFn: () => dataApi.getSources(),
    staleTime: 30000, // 30 seconds
    retry: 2,
  });

  // Test connection mutation
  const testConnectionMutation = useMutation({
    mutationFn: (id: string) => dataApi.testConnection(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dataSources'] });
    },
  });

  // Discover schema mutation
  const discoverSchemaMutation = useMutation({
    mutationFn: (id: string) => dataApi.discoverSchema(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dataSources'] });
    },
  });

  // Create data source mutation
  const createSourceMutation = useMutation({
    mutationFn: (source: Parameters<typeof dataApi.createSource>[0]) =>
      dataApi.createSource(source),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dataSources'] });
    },
  });

  // Delete data source mutation
  const deleteSourceMutation = useMutation({
    mutationFn: (id: string) => dataApi.deleteSource(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dataSources'] });
    },
  });

  return {
    // Data
    sources: sourcesQuery.data || [],
    isLoading: sourcesQuery.isLoading,
    isError: sourcesQuery.isError,
    error: sourcesQuery.error,

    // Actions
    refetch: sourcesQuery.refetch,
    testConnection: testConnectionMutation.mutate,
    discoverSchema: discoverSchemaMutation.mutate,
    createSource: createSourceMutation.mutate,
    deleteSource: deleteSourceMutation.mutate,

    // Mutation states
    isTestingConnection: testConnectionMutation.isPending,
    isDiscovering: discoverSchemaMutation.isPending,
    isCreating: createSourceMutation.isPending,
    isDeleting: deleteSourceMutation.isPending,
  };
}

export function useDataSource(id: string) {
  return useQuery({
    queryKey: ['dataSource', id],
    queryFn: () => dataApi.getSource(id),
    enabled: !!id,
    staleTime: 30000,
  });
}

export default useDataSources;
